import { FilterChip } from '@/components/ui';
import { useStore } from '@/providers/StoreProvider';
import React, { useState, useEffect, useRef } from 'react';
import {
  useFiltersConfig,
  type FilterKey,
} from '@/features/listings/filters/hooks/useFiltersConfig';
import { useFilterComponents } from '@/features/listings/filters/hooks/useFilterComponents';
import { useActiveFiltersCount } from '@/features/listings/filters/hooks/useActiveFiltersCount';
import { useAnalytics } from '@/hooks/useAnalytics';
import { RentStabilizedDownloadModal } from '@/components/modal/RentStabilizedDownloadModal';

export function FiltersBarMobile() {
  const [openFilter, setOpenFilter] = useState<FilterKey | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const analytics = useAnalytics();
  // Rent-stab filter hijack — see FiltersBar.tsx for full context. On
  // web + mobile-web, clicking the chip opens a download-prompt modal
  // instead of applying the filter, because the full rent-stab
  // experience lives in the app.
  const [rentStabModalOpen, setRentStabModalOpen] = useState(false);

  // Shared hooks
  const { filters } = useFiltersConfig();
  const { getFilterComponent } = useFilterComponents('mobile');

  const fetchNeighborhoods = useStore(s => s.neighborhoods.fetchNeighborhoods);

  useEffect(() => {
    void fetchNeighborhoods();
  }, [fetchNeighborhoods]);

  // Override vaul's scroll prevention
  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    const bypassScrollLock = (e: Event) => e.stopPropagation();

    // Allow horizontal scrolling by bypassing vaul's prevention
    element.addEventListener('wheel', bypassScrollLock, { capture: true });
    element.addEventListener('touchmove', bypassScrollLock, { capture: true });

    return () => {
      element.removeEventListener('wheel', bypassScrollLock, { capture: true });
      element.removeEventListener('touchmove', bypassScrollLock, { capture: true });
    };
  }, []);

  const openFilterSheet = React.useCallback(
    (key: FilterKey) => {
      // Best-effort mapping for mobile (depends on filter key naming in config)
      if (String(key).toLowerCase().includes('location')) {
        analytics.track('listings_filter_neighborhood_open');
      }
      if (String(key).toLowerCase().includes('all')) {
        analytics.track('listings_filter_all_open');
      }
      switch (key) {
        case 'price':
          analytics.track('listings_quickfilter_price');
          break;
        case 'beds':
          analytics.track('listings_quickfilter_beds');
          break;
        case 'moveInDate':
          analytics.track('listings_quickfilter_movein');
          break;
        case 'verified':
          analytics.track('listings_quickfilter_verified');
          break;
        case 'topRated':
          analytics.track('listings_quickfilter_toprated');
          break;
        case 'rentStabilized':
          analytics.track('listings_quickfilter_rentstabilized');
          // TEMP: hijack the rent-stab filter to open the app-download
          // modal instead of applying the filter on web. See parallel
          // hijack in FiltersBar.tsx. Restore existing behavior by
          // removing the two lines below and un-commenting the
          // setOpenFilter call at the end of this function.
          setRentStabModalOpen(true);
          return;
        // break;
        case 'goodCause':
          analytics.track('listings_quickfilter_goodcause');
          break;
        default:
          break;
      }
      setOpenFilter(key);
    },
    [analytics],
  );

  return (
    <>
      <section
        ref={sectionRef}
        className="flex gap-2 overflow-x-auto hide-scrollbar px-6 pb-4"
        style={{
          pointerEvents: 'auto',
          touchAction: 'pan-x',
          zIndex: 30,
        }}
      >
        {filters.map(({ key, label, anchorRef, icon, isActive }) => (
          <div key={key} ref={anchorRef}>
            <FilterChip
              onClick={() => openFilterSheet(key)}
              label={label}
              isOpen={openFilter === key}
              leadingIcon={icon}
              active={openFilter === key || !!isActive}
            />
          </div>
        ))}
      </section>

      {/* Mobile Filter Components */}
      {filters.map(({ key, anchorRef }) => (
        <div key={key}>
          {getFilterComponent(key, {
            open: openFilter === key,
            onClose: () => {
              if (String(key).toLowerCase().includes('location')) {
                analytics.track('listings_filter_neighborhood_close');
              }
              if (String(key).toLowerCase().includes('all')) {
                analytics.track('listings_filter_all_close');
              }
              setOpenFilter(null);
            },
          })}
        </div>
      ))}

      {/* Rent-stabilized filter opens this modal instead of the filter
          sheet — see openFilterSheet switch above. */}
      <RentStabilizedDownloadModal
        open={rentStabModalOpen}
        onClose={() => setRentStabModalOpen(false)}
        source="listings_filter_chip_mobile"
      />
    </>
  );
}
